import { useMutation } from "@tanstack/react-query"
import { otpVerify } from "api/auth.api.ts"
import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { AppButton } from "ui/base/appButton/AppButton.jsx"
import { AppButtonAuth } from "ui/base/appButton/AppButtonAuth.jsx"
import { HelperText } from "ui/base/helperText/HelperText"
import { themeColor, themeFontSize } from "ui/theme/index.js"

export const PasswordResetOtp = ({
    email,
    setActiveTab,
    setState
}) => {
    const [num1State, setNum1State] = useState("")
    const [num2State, setNum2State] = useState("")
    const [num3State, setNum3State] = useState("")
    const [num4State, setNum4State] = useState("")

    const [otpError, setOtpError] = useState({ value: false, text: "" })

    const num1 = useRef(null)
    const num2 = useRef(null)
    const num3 = useRef(null)
    const num4 = useRef(null)

    const {
        mutate: mutateOtpVerify,
        isLoading: isOtpVerifyLoading,
        isSuccess,
    } = useMutation({
        mutationFn: otpVerify,

        onError: (err: any) => {
            setOtpError({
                value: true,
                text: err?.response?.data?.message
                    ? err?.response?.data?.message
                    : "This code is invalid",
            })
        },
    })

    useEffect(() => {
        if (isSuccess) {
            setActiveTab("ResetPassword");
            setState(email);
        }
    }, [isSuccess])

    const getVerifyCodeAlertMessage = (msg) => {
        if (msg === "Verification code expired") {
            return "Code expired. Go back and confirm your email address."
        } else {
            return msg
        }
    }

    const onVerifyOtp = () => {
        if ((num1State + num2State + num3State + num4State).length !== 4) {
            setOtpError({ value: true, text: "Fill all the fields" })
        } else {
            mutateOtpVerify({
                email: email,
                otp: `${num1State}${num2State}${num3State}${num4State}`,
                new_signup: false,
            })
        }
    }

    if (!email) {
        return null;
    }

    return (
        <OtpContainer>
            <OtpTitleContainer>
                <HelperText
                    size='h5'
                    sizeMobile='h5'
                    weight={600}
                    centerMobile
                    center
                    style={{ whiteSpace: "nowrap" }}>
                    Password reset
                </HelperText>
                <HelperText centerMobile center>
                    Please enter the code sent to your email to reset password
                </HelperText>
            </OtpTitleContainer>
            <OptCodeContainer>
                <OtpInputContainer>
                    <OtpBox
                        type='number'
                        inputmode='numeric'
                        maxLength={1}
                        value={num1State}
                        ref={num1}
                        onChange={(e) => {
                            const value = e.target.value
                            if (value.length <= 1) {
                                value !== "" ? num2.current?.focus() : null
                                setNum1State(value)
                                setOtpError({ value: false, text: "" })
                            }
                        }}
                    />
                    <OtpBox
                        type='number'
                        inputmode='numeric'
                        maxLength={1}
                        value={num2State}
                        ref={num2}
                        onChange={(e) => {
                            const value = e.target.value
                            if (value.length <= 1) {
                                value !== "" ? num3.current?.focus() : num1.current?.focus()
                                setNum2State(value)
                                setOtpError({ value: false, text: "" })
                            }
                        }}
                    />
                    <OtpBox
                        type='number'
                        inputmode='numeric'
                        maxLength={1}
                        value={num3State}
                        ref={num3}
                        onChange={(e) => {
                            const value = e.target.value
                            if (value.length <= 1) {
                                value !== "" ? num4.current?.focus() : num2.current?.focus()
                                setNum3State(value)
                                setOtpError({ value: false, text: "" })
                            }
                        }}
                    />
                    <OtpBox
                        type='number'
                        inputmode='numeric'
                        maxLength={1}
                        value={num4State}
                        ref={num4}
                        onChange={(e) => {
                            const value = e.target.value
                            if (value.length <= 1) {
                                value !== "" ? null : num3.current?.focus()
                                setNum4State(value)
                                setOtpError({ value: false, text: "" })
                            }
                        }}
                    />
                </OtpInputContainer>
                {otpError.value && (
                    <HelperText color='pink' size='small' centerMobile center>
                        {getVerifyCodeAlertMessage(otpError.text)}
                    </HelperText>
                )}

                <AppButtonAuth
                    onClick={onVerifyOtp}
                    disabled={isOtpVerifyLoading}>
                    Reset password
                </AppButtonAuth>
            </OptCodeContainer>
        </OtpContainer>
    )
}

const OtpContainer = styled.div`
  padding: 1rem;
  width: 100%;
  max-width: 450px;
  margin-inline: auto;
  display: grid;
  gap: 50px;
  font-family: var(--font-secondary);
  margin-top: 2dvh;
  @media (min-width: 768px) {
    margin-top: 0px;
  }
`

const OptCodeContainer = styled.div`
  display: grid;
  gap: 1rem;
`

const OtpTitleContainer = styled.div`
  text-align: center;
  display: grid;
  gap: 1rem;
`

const OtpInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
`
const OtpBox = styled.input`
  width: 100%;
  aspect-ratio: 1/1;
  padding: 10px;
  text-align: center;
  font-size: ${themeFontSize("medium")};
  font-weight: 600;
  border-width: 2px;
  border-style: solid;
  border-color: ${themeColor("border")};
  border-radius: 8px;
  outline: none;
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`
